import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { CHART_RIGHT_SPACE_WIDTH, CHART_RIGHT_SPACE_LEFT_PADDING } from '../../common/constants';
import type {
	DeploymentFrequencyReportWeekData,
	EChartsEvent,
	IssuesPerDeploymentDataPoint,
} from '../../common/types';
import { CHART_HEIGHT } from '../../common/ui/issues-per-deployment-chart/constants';
import { useSelectedWeekStore } from '../../controllers/selected-date-range';
import {
	getSelectedWeekData,
	useSmartWeekSelect,
} from '../../controllers/selected-date-range/utils';
import { useReportCoreData } from '../../services/deployment-frequency-core';
import { getProdDataState } from '../../services/deployment-frequency-core/utils';
import Chart from './chart';
import { convertReportCoreToChartDataPoints } from './chart/utils';
import { LoadingSkeleton } from './loading-skeleton';
import messages from './messages';
import { SelectionDetail } from './selection-detail';

const Title = () => {
	const { formatMessage } = useIntl();
	return (
		<ChartTitleContainer>
			<Heading size="xsmall">{formatMessage(messages.chartTitle)}</Heading>
			<Tooltip content={formatMessage(messages.titleHelpTooltip)} position="right">
				<QuestionIcon>
					<QuestionCircleIcon size="small" label="" />
				</QuestionIcon>
			</Tooltip>
		</ChartTitleContainer>
	);
};

export const IssuesPerDeploymentChart = () => {
	const { data, loading, error } = useReportCoreData();
	const [{ selectedWeekStart }, { setSelectedWeekStart }] = useSelectedWeekStore();
	const onChartClicked = useCallback(
		({ data: eventData }: EChartsEvent<IssuesPerDeploymentDataPoint>) => {
			setSelectedWeekStart(eventData.customInfo.startDateInclusive);
		},
		[setSelectedWeekStart],
	);
	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);
	// @ts-expect-error - TS2345 - Argument of type '(newWeekStartDate: Date) => void | Promise<void>' is not assignable to parameter of type '(newWeekStartDate: Date) => Action<State>'.
	useSmartWeekSelect(data, selectedWeekStart, setSelectedWeekStart);

	if (loading) {
		return <LoadingSkeleton />;
	}
	if (error && data == null) {
		// When error happens, retry button will be displayed by the bar chart together as two charts use the same service
		return null;
	}
	if (data == null || selectedWeekStart === null) {
		return <LoadingSkeleton />;
	}
	if (error) {
		// TODO - Probably we would want to fire analytics?
	}
	if (data.hasDeploymentProvider === false) {
		// when deployment provider is missing, the installation guide will be displayed by the bar chart.
		return null;
	}

	const prodDataState = getProdDataState(data);
	const dataPoints = convertReportCoreToChartDataPoints(data, selectedWeekStart);
	const selectedWeekData: DeploymentFrequencyReportWeekData = getSelectedWeekData(
		data.weeksData,
		selectedWeekStart,
	);

	return (
		<>
			<Title />
			<ChartContainer data-test-id="software-deployment-frequency-report.ui.issues-per-deployment-chart">
				<ChartItem>
					<Chart
						dataPoints={dataPoints}
						medianIssues={
							prodDataState === 'SUFFICIENT_FOR_TREND' ? data.medianIssuesPerDeployment : null
						}
						onEvents={events}
					/>
				</ChartItem>
				<SelectionDetailItem>
					<SelectionDetail
						medianIssuesPerDeployment={selectedWeekData.medianIssuesPerDeployment.prod}
						wholePeriodMedian={data.medianIssuesPerDeployment}
						prodDataState={prodDataState}
					/>
				</SelectionDetailItem>
			</ChartContainer>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartTitleContainer = styled.div({
	height: token('space.300', '24px'),
	display: 'flex',
	alignItems: 'center',
	margin: `0 0 ${token('space.100', '8px')} 0`,
	paddingLeft: `${token('space.250', '20px')}`,
	marginBottom: `${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuestionIcon = styled.div({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
	paddingTop: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${CHART_HEIGHT}px`,
	marginBottom: `${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartItem = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${CHART_RIGHT_SPACE_WIDTH}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectionDetailItem = styled.div({
	width: '224px',
	minWidth: '224px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${CHART_RIGHT_SPACE_LEFT_PADDING}px`,
});
